import { watch } from "vue";
import {
  handleBye,
  handleChat,
  handleChatActivity,
  handleGlobalChat,
  handleLock,
  handleNominationMsg,
  handlePing,
  handleRochambo,
  handleRochamboPick,
  handleVote,
  updateEdition,
  updateFabled,
  updateGameState,
  updateHandRaisedAt,
  updatePlayer,
  updatePlayerHand,
  updatePlayerName,
  updatePlayerPronouns,
  updateSeat,
  sendGameState,
} from '@/services/sockethandlers'
import {
  useChatStore,
  useGrimoireStore,
  useLocaleStore,
  usePlayersStore,
  useSessionStore,
  useSignalsStore,
  useSoundboardStore,
  useUserPreferencesStore,
  useVoiceChatStore,
  useVotingStore,
  useDialogStore,
} from "@/stores";
import type {
  Alignment,
  ChatMessage,
  Edition,
  GamePhase,
  JukeboxSound,
  Nomination,
  Player,
  Role,
  SignalPayload,
  SocketCommands,
  VoiceTown,
  VoteHistoryEntry,
} from "@/types";

type MessageHandler = (params: unknown) => void | Promise<void>;

export class LiveSession {
  private readonly _wss: string;
  private _socket: WebSocket | null;
  private _isPlayerOrSpectator: boolean;
  public gamestate: Array<{
    name: string;
    id: string;
    isDead: boolean;
    voteToken: boolean;
    pronouns: string;
    roleId?: string;
    secretRole: boolean;
    discordTag?: string | undefined;
    handRaisedAt?: number | null;
  }>;
  public readonly pingInterval: number;
  private _pingTimer: ReturnType<typeof setTimeout> | null;
  private _reconnectTimer: ReturnType<typeof setTimeout> | null;
  public connectedAt: number;
  public players: Record<string, number>;
  public pings: Record<string, number>;
  private _messageHandlers: Map<string, MessageHandler>;

  constructor() {
    this._wss =
      import.meta.env["VITE_SERVER_URL"];
    this._socket = null;
    this._isPlayerOrSpectator = true;
    this.gamestate = [];
    this.pingInterval = 30 * 1000; // 30 seconds between pings
    this._pingTimer = null;
    this._reconnectTimer = null;
    this.connectedAt = 0; // timestamp of the last successful connection
    this.players = {}; // map of players connected to a session
    this.pings = {}; // map of player IDs to ping
    this._messageHandlers = new Map();
  }

  initialize() {
    const chatStore = useChatStore();
    const grimoire = useGrimoireStore();
    const localeStore = useLocaleStore();
    const playersStore = usePlayersStore();
    const session = useSessionStore();
    const signalsStore = useSignalsStore();
    const soundboardStore = useSoundboardStore();
    const voiceChatStore = useVoiceChatStore();
    const votingStore = useVotingStore();

    session.initializeFromUrl();
    if (session.sessionId) {
      this.connect(session.sessionId);
    }

    watch(
      () => session.sessionId,
      (newId) => {
        if (newId) {
          this.connect(newId);
        } else {
          this.disconnect();
        }
      }
    );

    watch(
      () => localeStore.currentLanguage,
      (newLocale) => {
        if (!this._isPlayerOrSpectator) {
          this.send("locale", newLocale);
        }
      }
    );
    this._messageHandlers = new Map<string, MessageHandler>([
      ["getGamestate", (params) => sendGameState(params as string | undefined, false)],
      ["edition", (params) => updateEdition(params as { edition: Edition; roles?: Array<Role> })],
      ["fabled", (params) => updateFabled(params as Array<Role | { id: string; isCustom?: boolean }>)],
      ["gs", async (params) => await updateGameState(params as Record<string, unknown>)],
      ["player", (params) => updatePlayer(params as { index: number; property: keyof Player; value: unknown })],
      ["claim", (params) => updateSeat(params as [number, string])],
      ["ping", (params) => handlePing(params as [(number | undefined)?, unknown?] | undefined)],
      ["nomination", (params) => handleNominationMsg(params as Nomination | null)],
      ["swap", (params) => playersStore.swap(params as [number, number])],
      ["move", (params) => playersStore.move(params as [number, number])],
      ["remove", (params) => playersStore.remove(params as number)],
      ["marked", (params) => votingStore.setMarkedPlayer(params as number)],
      ["newGame", () => grimoire.newGame()],
      ["gamePhase", (params) => grimoire.setGamePhase(params as GamePhase)],
      ["annouceWinner", (params) => grimoire.announceWinner(params as Alignment)],
      ["dayCount", (params) => grimoire.setDayCount(params as number)],
      ["allowSelfNaming", (params) => grimoire.setAllowSelfNaming(!!params)],
      ["isSecretVoteMode", (params) => grimoire.setSecretVote(params as boolean)],
      ["isSecretVote", (params) => grimoire.setSecretVote(params as boolean)],
      ["isTextChatAllowed", (params) => grimoire.setAllowTextChat(params as boolean)],
      ["isWhisperAllowed", (params) => grimoire.setAllowWhisper(params as boolean)],
      ["isSignalHistoryAllowed", (params) => grimoire.setAllowSignalHistory(params as boolean)],
      ["chatActivity", (params) => handleChatActivity(params as { from: string; to: string })],
      ["playSound", (params) => soundboardStore.playSound(params as { sound: JukeboxSound })],
      ["setTimer", (params) => grimoire.setTimer(params as { name?: string; duration?: number })],
      ["isVoteHistoryAllowed", (params) => { grimoire.setVoteHistoryAllowed(params as boolean); votingStore.clearVoteHistory(); }],
      ["votingSpeed", (params) => votingStore.setVotingSpeed(params as number)],
      ["clearChat", () => chatStore.clearMessages()],
      ["clearRoles", () => playersStore.clearRoles(true)],
      ["clearVoteHistory", () => votingStore.clearVoteHistory()],
      ["isVoteInProgress", (params) => votingStore.setVoteInProgress(params as boolean)],
      ["rochambo", (params) => handleRochambo(params)],
      ["rochamboPick", (params) => handleRochamboPick(params)],
      ["vote", (params) => handleVote(params as [number, number, boolean])],
      ["lock", (params) => handleLock(params as [number, number])],
      ["bye", (params) => handleBye(params as string)],
      ["pronouns", (params) => updatePlayerPronouns(params as [number, string])],
      ["name", (params) => updatePlayerName(params as [number, string])],
      ["raisedHands", (params) => updatePlayerHand(params as [number, number])],
      ["handRaisedAt", (params) => updateHandRaisedAt(params as [number, number | null])],
      ["locale", (params) => localeStore.forceLocale(params as string)],
      ["voteHistory", (params) => votingStore.setVoteHistory(params as VoteHistoryEntry[])],
      ["town", (params) => voiceChatStore.setTown(params as VoiceTown)],
      ["chat", (params) => handleChat(params as ChatMessage)],
      ["globalChat", (params) => handleGlobalChat(params as ChatMessage)],
      ["clearHands", () => playersStore.clearHands(true)],
      ["signal", (params) => signalsStore.receiveSignal(params as SignalPayload)],
    ]);
  }

  send(command: SocketCommands, params: unknown = null) {
    if (this._socket?.readyState === 1) {
      this._socket.send(JSON.stringify([command, params]));
    }
  }

  sendDirect(
    playerId: string | undefined | null,
    command: SocketCommands,
    params: unknown
  ) {
    if (playerId) {
      this.send("direct", { [playerId]: [command, params] });
    } else {
      this.send(command, params);
    }
  }

  _onOpen() {
    const sessionStore = useSessionStore();
    this.connectedAt = Date.now();
    if (this._isPlayerOrSpectator) {
      this.sendDirect("host", "getGamestate", sessionStore.playerId);
    } else {
      sendGameState();
    }
    this._ping();
  }

  _ping() {
    const sessionStore = useSessionStore();
    handlePing();
    this.send("ping", [
      this._isPlayerOrSpectator
        ? sessionStore.playerId
        : Object.keys(this.players).length,
      "latency",
    ]);
    if (this._pingTimer) clearTimeout(this._pingTimer);
    this._pingTimer = setTimeout(this._ping.bind(this), this.pingInterval);
  }

  async _handleMessage({ data }: MessageEvent) {
    let command: string,
      params: unknown;
    try {
      [command, params] = JSON.parse(data);
    } catch (err) {
      console.error("unsupported socket message", err, data);
      return;
    }

    const handler = this._messageHandlers.get(command);
    if (handler) {
      await handler(params);
    }
  }

  connect(channel: string) {
    const sessionStore = useSessionStore();
    const voiceChatStore = useVoiceChatStore();
    if (!sessionStore.playerId) {
      sessionStore.setPlayerId(Math.random().toString(36).substring(2));
    }
    this.pings = {};
    voiceChatStore.setTown(null);
    sessionStore.setPlayerCount(0);
    sessionStore.setPing(0);
    this._isPlayerOrSpectator = sessionStore.isPlayerOrSpectator;
    this._open(channel);
  }

  /**
 * Open a new session for the passed channel.
 * @param channel
 * @private
 */
  _open(channel: string) {
    const sessionStore = useSessionStore();
    this.disconnect();
    this._socket = new WebSocket(
      this._wss +
      channel +
      "/" +
      (this._isPlayerOrSpectator ? sessionStore.playerId : "host") +
      "/" +
      sessionStore.password
    );
    this._socket.addEventListener("message", this._handleMessage.bind(this));
    this._socket.onopen = this._onOpen.bind(this);
    this._socket.onclose = (err: CloseEvent) => {
      this._socket = null;
      if (this._pingTimer) clearInterval(this._pingTimer);
      this._pingTimer = null;
      if (err.code === 1000) {
        sessionStore.setSessionId("");
        if (err.reason) useDialogStore().alert(err.reason);
      } else {
        // connection interrupted, reconnect after 3 seconds
        sessionStore.setReconnecting(true);
        this._reconnectTimer = setTimeout(
          () => this.connect(channel),
          3 * 1000
        );
      }
    };
  }

  disconnect() {
    const sessionStore = useSessionStore();
    const voiceChatStore = useVoiceChatStore();
    this.pings = {};
    voiceChatStore.setTown(null);
    sessionStore.setPlayerCount(0);
    sessionStore.setPing(0);
    sessionStore.setReconnecting(false);
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    if (this._socket) {
      if (this._isPlayerOrSpectator) {
        this.sendDirect("host", "bye", sessionStore.playerId);
      }
      this._socket.close(1000);
      this._socket = null;
    }
    const localeStore = useLocaleStore();
    localeStore.revertLocale();
  }

  requestGamestate() {
    const sessionStore = useSessionStore();
    if (this._isPlayerOrSpectator && sessionStore.sessionId) {
      this.sendDirect("host", "getGamestate", sessionStore.playerId);
    }
    this._autoClaimSeatByDiscordTag();
  }

  _autoClaimSeatByDiscordTag() {
    const playersStore = usePlayersStore();
    const sessionStore = useSessionStore();
    const userPreferences = useUserPreferencesStore();
    if (!sessionStore.isPlayerOrSpectator) return;
    if (!userPreferences.discordName) return;
    const matchIndex = playersStore.players.findIndex(
      (p: Player) => p.discordTag === userPreferences.discordName && !p.id
    );
    if (matchIndex === -1) return;
    sessionStore.claimSeat(matchIndex);
  }

  sendEdition(playerId = "") {
    if (this._isPlayerOrSpectator) return;
    const grimoireStore = useGrimoireStore();
    const { edition } = grimoireStore;
    let roles;
    if (edition && !edition.isOfficial) {
      roles = Array.from<Role>(grimoireStore.roles.values()).map((role: Role) =>
        role?.isCustom ? role : { id: role?.id }
      );
    }
    this.sendDirect(playerId, "edition", {
      edition,
      ...(roles ? { roles } : {}),
    });
  }
}

export default new LiveSession();
